import { z } from 'zod';

export const AppointmentMessageKind = z.enum(['text', 'slot-proposal', 'system-event']);
export type AppointmentMessageKind = z.infer<typeof AppointmentMessageKind>;

export const AppointmentParticipantRole = z.enum(['coordinator', 'vendor', 'operator', 'system']);
export type AppointmentParticipantRole = z.infer<typeof AppointmentParticipantRole>;

export const TextMessagePayload = z.object({
  body: z.string().min(1).max(4000),
});
export type TextMessagePayload = z.infer<typeof TextMessagePayload>;

export const SlotProposalCandidate = z
  .object({
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    note: z.string().max(200).optional(),
  })
  .refine((c) => Date.parse(c.endsAt) > Date.parse(c.startsAt), {
    message: 'endsAt must be after startsAt',
  });
export type SlotProposalCandidate = z.infer<typeof SlotProposalCandidate>;

export const SlotProposalPayload = z.object({
  candidates: z.array(SlotProposalCandidate).min(2).max(8),
  bookedCandidateIndex: z.number().int().min(0).nullable().default(null),
});
export type SlotProposalPayload = z.infer<typeof SlotProposalPayload>;

export const SystemEventKind = z.enum([
  'thread.opened',
  'thread.closed',
  'appointment.booked',
  'appointment.transitioned',
  'lockbox.revealed',
  'proposal.expired',
  'magic-link.sent',
]);
export type SystemEventKind = z.infer<typeof SystemEventKind>;

export const SystemEventPayload = z.object({
  eventKind: SystemEventKind,
  data: z.record(z.unknown()).default({}),
});
export type SystemEventPayload = z.infer<typeof SystemEventPayload>;

const baseFields = {
  id: z.string().uuid(),
  appointmentId: z.string().uuid(),
  authorUserId: z.string().uuid(),
  authorRole: AppointmentParticipantRole,
  createdAt: z.string().datetime(),
};

export const AppointmentMessage = z.discriminatedUnion('kind', [
  z.object({ ...baseFields, kind: z.literal('text'), payload: TextMessagePayload }),
  z.object({ ...baseFields, kind: z.literal('slot-proposal'), payload: SlotProposalPayload }),
  z.object({ ...baseFields, kind: z.literal('system-event'), payload: SystemEventPayload }),
]);
export type AppointmentMessage = z.infer<typeof AppointmentMessage>;

export const ClientAppointmentMessageInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), payload: TextMessagePayload }),
  z.object({
    kind: z.literal('slot-proposal'),
    payload: SlotProposalPayload.omit({ bookedCandidateIndex: true }),
  }),
]);
export type ClientAppointmentMessageInput = z.infer<typeof ClientAppointmentMessageInput>;
